import {Button, MultiChoice, type Option} from 'platform/components';
import {Box, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {WorkshopDashboardFilters} from '../types/WorkshopDashboardFilters';

export interface WorkshopOverviewFiltersProps extends TestIdProps {
  filters: WorkshopDashboardFilters;
  branchOptions: Option[];
  orderTypeOptions: Option[];
  variantOptions: Option[];
  isLoading?: boolean;
  isError?: boolean;
  onFilterChange: (key: keyof WorkshopDashboardFilters, values: string[] | null) => void;
  onClearFilters: () => void;
}

const FIELD_MIN_WIDTH = 44;

export function WorkshopOverviewFilters(props: WorkshopOverviewFiltersProps) {
  const hasFilters = Object.values(props.filters).some((values) => values.length > 0);
  const placeholder = match(props)
    .with({isError: true}, () => i18n.t('general.labels.error'))
    .with({isLoading: true}, () => i18n.t('general.labels.loading'))
    .otherwise(() => i18n.t('general.labels.all'));

  return (
    <Box
      padding={4}
      borderRadius="medium"
      border="thin solid"
      borderColor="palettes.neutral.40.100"
      backgroundColor="palettes.neutral.10.100"
      data-testid={suffixTestId('workshopOverviewFilters', props)}
    >
      <HStack spacing={3} align="flex-start" wrap>
        <Box flex={1} minWidth={FIELD_MIN_WIDTH} maxWidth="100%">
          <MultiChoice
            label={i18n.t('entity.warehouse.labels.branch')}
            placeholder={placeholder}
            value={props.filters.branchId}
            options={props.branchOptions}
            onChange={(value) => props.onFilterChange('branchId', value)}
            isLoading={props.isLoading}
            isDisabled={props.isLoading || props.isError}
            hasOptionCheckbox
            closeMenuOnSelect={false}
            menuInPortal
          />
        </Box>
        <Box flex={1} minWidth={FIELD_MIN_WIDTH} maxWidth="100%">
          <MultiChoice
            label={i18n.t('entity.warehouse.labels.orderType')}
            placeholder={placeholder}
            value={props.filters.serviceOrderTypeId}
            options={props.orderTypeOptions}
            onChange={(value) => props.onFilterChange('serviceOrderTypeId', value)}
            isLoading={props.isLoading}
            isDisabled={props.isLoading || props.isError}
            hasOptionCheckbox
            closeMenuOnSelect={false}
            menuInPortal
          />
        </Box>
        <Box flex={1} minWidth={FIELD_MIN_WIDTH} maxWidth="100%">
          <MultiChoice
            label={i18n.t('entity.order.labels.orderVariant')}
            placeholder={placeholder}
            value={props.filters.serviceOrderVariantId}
            options={props.variantOptions}
            onChange={(value) => props.onFilterChange('serviceOrderVariantId', value)}
            isLoading={props.isLoading}
            isDisabled={props.isLoading || props.isError}
            hasOptionCheckbox
            closeMenuOnSelect={false}
            menuInPortal
          />
        </Box>
        <Box paddingTop={6}>
          <Button
            title={i18n.t('page.datagrid.labels.clearAll')}
            variant="ghostLink"
            size="small"
            isDisabled={!hasFilters}
            onClick={props.onClearFilters}
          />
        </Box>
      </HStack>
    </Box>
  );
}
